import { FC, useCallback } from 'react';
import { Stack, Typography } from '@mui/material';
import { AttachFileOutlined } from '@mui/icons-material';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined';

import { ID } from 'domain/types/ID';
import { Attachment } from 'domain/entities/Attachment';
import { useCaseGetAttachmentsPerJobs } from 'application/attachments/useCases/useCaseGetAttachmentsPerJob';
import { downloadImage } from 'infrastructure/utils/downloadFile';
import { Loader, IconButton } from 'targets/web/components';
import { NoAttachments } from 'targets/web/modules/jobs/components/JobDetailsForm/AttachmentsJobDetails';

interface CustomerAttachmentCardProps {
  attachment: Attachment & { url: string };
}

const CustomerAttachmentCard: FC<CustomerAttachmentCardProps> = ({ attachment }) => {
  const onPreview = useCallback(() => {
    window.open(attachment.url, '_blank');
  }, [attachment.url]);

  const downloadImageHandler = useCallback(
    () => downloadImage(attachment.url, attachment.name),
    [attachment.name, attachment.url],
  );

  return (
    <Stack direction="row" justifyContent="space-between" gap={2}>
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        sx={{
          paddingInline: 2,
          paddingBlock: 1,
          borderRadius: 1,
          borderColor: 'outline',
          borderStyle: 'solid',
          borderWidth: 1,
          width: '100%',
          cursor: 'default',
        }}
      >
        <Stack
          sx={{
            padding: 1.5,
            borderRadius: 1,
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '32px',
            backgroundColor: 'primary.action.selected',
          }}
        >
          <AttachFileOutlined sx={{ color: 'primary.main', fontSize: '20px' }} />
        </Stack>

        <Stack>
          <Typography variant="labelSmall">{attachment.name}</Typography>
          <Typography variant="caption" color="secondary">
            {attachment.contentType}
          </Typography>
        </Stack>
      </Stack>

      <IconButton variant="outlined" color="primary" onClick={onPreview}>
        <RemoveRedEyeOutlinedIcon />
      </IconButton>

      <IconButton variant="outlined" color="secondary" onClick={downloadImageHandler}>
        <GetAppOutlinedIcon />
      </IconButton>
    </Stack>
  );
};

interface Props {
  jobId: ID;
}

const CustomerAttachmentsSection = ({ jobId }: Props) => {
  const {
    items: attachments,
    isLoading,
    isFetching,
    total,
  } = useCaseGetAttachmentsPerJobs({
    jobId,
    type: ['customer', 'custom'],
  });

  if (isLoading || (total === 0 && isFetching)) {
    return <Loader />;
  }

  return (
    <>
      <Typography variant="h6">Attachments</Typography>
      {attachments && attachments?.length > 0 && (
        <Stack gap={4}>
          {attachments.map((attachment) => (
            <CustomerAttachmentCard key={attachment.id} attachment={attachment} />
          ))}
        </Stack>
      )}
      {attachments && attachments?.length === 0 && <NoAttachments />}
    </>
  );
};

export default CustomerAttachmentsSection;
